import {EdgeStyle} from '../canvas/styles/edge-style';
import {MODEL_COLOURS} from './model-item-enums'
import { Injectable } from '@angular/core';
import { EDGE_TYPE, Linqset, LinqsetEdge, LinqsetNode, createLinqsetEdge, getEdgeType } from "../common/linqset-model";
import { v4 } from 'uuid';
import { LinqsetNodeVisualisation, createModelEdge } from '../canvas/model/graph-model';
import { ArrowType, EdgeStyleBase, IGraph, INode } from '@yfiles/yfiles';

@Injectable({
    providedIn: 'root'
})
export class EdgeService {
    // one style per edge type, shared by every edge of that type
    private readonly edgeStyleList = new Map<EDGE_TYPE, EdgeStyleBase>([
        [EDGE_TYPE.MODEL, new EdgeStyle(MODEL_COLOURS.MODEL_NODE_COLOR, MODEL_COLOURS.MODEL_NODE_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.PERFORM, new EdgeStyle(MODEL_COLOURS.ROLE_COLOR, MODEL_COLOURS.ROLE_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.FULFILS, new EdgeStyle(MODEL_COLOURS.PEOPLE_COLOR, MODEL_COLOURS.PEOPLE_HOVER_COLOR, ArrowType.DIAMOND)],
        [EDGE_TYPE.CREATE, new EdgeStyle(MODEL_COLOURS.ACTION_COLOR, MODEL_COLOURS.ACTION_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.SUPPLY, new EdgeStyle(MODEL_COLOURS.SYSTEM_COLOR, MODEL_COLOURS.SYSTEM_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.USEBY, new EdgeStyle(MODEL_COLOURS.SYSTEM_COLOR, MODEL_COLOURS.SYSTEM_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.AUTOMATE, new EdgeStyle(MODEL_COLOURS.SYSTEM_COLOR, MODEL_COLOURS.SYSTEM_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.SUPPORT, new EdgeStyle(MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.JOINS, new EdgeStyle(MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR, ArrowType.TRIANGLE)],
        [EDGE_TYPE.USED_IN, new EdgeStyle(MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR, ArrowType.DIAMOND)],
        [EDGE_TYPE.PROVIDES, new EdgeStyle(MODEL_COLOURS.INFRASTRUCTURE_COLOR, MODEL_COLOURS.INFRASTRUCTURE_HOVER_COLOR, ArrowType.DIAMOND)],
        [EDGE_TYPE.ATTACHED, new EdgeStyle(MODEL_COLOURS.NOTES_HOVER_COLOR, MODEL_COLOURS.NOTES_HOVER_COLOR, ArrowType.DIAMOND)],
        [EDGE_TYPE.ACTION_SUMMARY, new EdgeStyle(MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR, ArrowType.DIAMOND)],
        [EDGE_TYPE.INFO_SUMMARY, new EdgeStyle(MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR, ArrowType.DIAMOND)],
        [EDGE_TYPE.INFO_ASSET, new EdgeStyle(MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR, ArrowType.DIAMOND)],
        [EDGE_TYPE.REALISE, new EdgeStyle(MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR, ArrowType.CHEVRON)],
    ])

    // a new edge between two model nodes, of the type their kinds make it
    createNewEdge(graph: IGraph, linqset:Linqset, sourceNode:INode, targetNode:INode) {
        const sourceVisualNode = sourceNode.tag as LinqsetNodeVisualisation
        const targetVisualNode = targetNode.tag as LinqsetNodeVisualisation

        const edgeType = this.getEdgeType(sourceVisualNode.node, targetVisualNode.node)
        const edgeStyle = this.getStyleForEdge(edgeType)

        const modelKey = (linqset.isOrganisationModel() && (edgeType == EDGE_TYPE.JOINS  || edgeType === EDGE_TYPE.USED_IN))?targetVisualNode.node.modelKey:linqset.modelKey
        const edgeData = {type:edgeType, modelKey, sourceKey: sourceVisualNode.node._key, targetKey:targetVisualNode.node._key, sourceVisualisationKey:sourceVisualNode.visualisationKey, targetVisualisationKey:targetVisualNode.visualisationKey} as LinqsetEdge

        const linqsetEdge = createLinqsetEdge(v4(), linqset.organisationKey, linqset.workspaceKey, edgeData)

        return graph.createEdge(sourceNode, targetNode, edgeStyle, createModelEdge(linqsetEdge))
    }

    // an edge for one the model already has
    createEdge(graph: IGraph, sourceNode:INode, targetNode:INode, edgeData: LinqsetEdge) {
        const source = sourceNode.tag?.node as LinqsetNode
        const target = targetNode.tag?.node as LinqsetNode

        // an edge with no type takes it from its ends; an end that isn't a model node (a model, say) has no kind to go by
        const edgeType = edgeData.type || (source && this.getEdgeType(source, target))
        const edgeStyle = this.getStyleForEdge(edgeType)

        return graph.createEdge(sourceNode, targetNode, edgeStyle, createModelEdge(edgeData))
    }

    // undefined for a type with no style of its own, so the graph's default is used
    getStyleForEdge(type:EDGE_TYPE):EdgeStyleBase | undefined {
        return this.edgeStyleList.get(type)
    }

    getEdgeType(sourceNode:LinqsetNode, targetNode?:LinqsetNode):EDGE_TYPE {
        return getEdgeType(sourceNode, targetNode)
    }
}
